import type {RenderableDisplayMode, RenderableObject} from '3d'
import {
  Matrix4,
  Vector3,
  Mesh,
  MeshPhongMaterial,
  MeshNormalMaterial,
  Object3D,
  BufferGeometry,
  Material,
  BackSide,
  Box3,
  type Side,
} from 'three'
import {logger} from '../../render/setup-globals'

const DEFAULT_SALMON_COLOR = 0xffa07a
const DEFAULT_BLACK_COLOR = 0x999999

type Options = {
  clone: boolean
  url: string
  name: string
  center: boolean
  scale: number
  scaleTo: number
  autoscale: boolean
  parts: Object3D[]
  mode: RenderableDisplayMode
  color: number
  specular: number
  transparent: boolean
  opacity: number
  side: Side
}

const defaultMaterialOptions = {
  color: DEFAULT_SALMON_COLOR,
  specular: DEFAULT_BLACK_COLOR,
  transparent: false,
  opacity: 1.0,
}

const defaultRenderableOptions = (id: number): Options => {
  return {
    clone: false,
    url: `x-renderable://${id}`,
    name: `model-${id}`,
    center: true,
    parts: [],
    scale: 1.0,
    scaleTo: 100,
    autoscale: false,
    mode: 'solid',
    side: BackSide,
    ...defaultMaterialOptions,
  }
}

class Renderable extends Object3D {
  geometry: BufferGeometry
  info: Options
  mesh: Mesh
  name: string
  url: string

  constructor(geometry: BufferGeometry, info: Partial<Options> = {}) {
    super()

    this.info = {...defaultRenderableOptions(this.id), ...info}
    this.geometry = this.info.clone ? geometry.clone() : geometry
    this.url = this.info.url
    this.name = this.info.name || this.info.url

    if (this.info.autoscale) {
      this.autoscale()
    }

    const mesh = new Mesh(geometry)

    // Add myself
    this.mesh = mesh
    this.mesh.name = this.name
    this.add(this.mesh)

    // Add any parts
    for (const part of this.info.parts) {
      if (!part) {
        continue
      }

      this.add(part)
    }

    if (this.info.center) {
      this.center()
    }

    this.switchMode(this.info.mode)
  }

  autoscale = () => {
    this.geometry.computeBoundingSphere()

    if (!this.geometry.boundingSphere) {
      return
    }

    if (this.geometry.boundingSphere.radius < this.info.scaleTo) {
      return
    }

    this.info.scale = this.info.scaleTo / this.geometry.boundingSphere.radius
    const scaleMat = new Matrix4().makeScale(this.info.scale, this.info.scale, this.info.scale)
    this.geometry.applyMatrix4(scaleMat)
    this.geometry.computeBoundingSphere()
  }

  center = () => {
    let bounds: Box3 | undefined
    this.traverse((o: RenderableObject) => {
      const geom = o.geometry

      if (!geom) {
        return
      }

      geom.computeBoundingBox()

      if (!geom.boundingBox) {
        return
      }

      if (bounds) {
        bounds.expandByPoint(geom.boundingBox.min)
        bounds.expandByPoint(geom.boundingBox.max)
      } else {
        bounds = geom.boundingBox.clone()
      }
    })

    // bounds should have been assigned during object traversal,
    // but bail in case they weren't.
    if (!bounds) {
      return
    }

    const bb = bounds.clone()
    const center = bounds.getCenter(new Vector3(-1, -1, -1)).negate()
    const min = bounds.min.negate()
    logger.debug(`Total centering transform: ${center.x}, ${center.y}, ${min.z}`)
    logger.debug('Bounds: ', bb)
    const transform = new Matrix4().makeTranslation(center.x, center.y, min.z)

    this.traverse((o: RenderableObject) => {
      // Only process the meshes actually in the scene
      // instead of also the renderable containers
      // to avoid applying a transform twice per container.
      if (o.mesh) {
        return
      }
      if (!o.geometry) {
        return
      }
      o.geometry.applyMatrix4(transform)
      o.geometry.computeBoundingBox()
      o.geometry.computeBoundingSphere()
    })
    return this
  }

  switchMode = (mode = 'solid') => {
    const materialOptions = {
      color: this.info.color || DEFAULT_SALMON_COLOR,
      specular: this.info.specular || DEFAULT_BLACK_COLOR,
      transparent: this.info.transparent != null ? this.info.transparent : false,
      opacity: this.info.opacity != null ? this.info.opacity : 1.0,
    }

    switch (mode) {
      case 'wireframe':
        this.mesh.material = new MeshPhongMaterial(materialOptions)
        ;(this.mesh.material as MeshPhongMaterial).wireframe = true
        break
      case 'solid':
        this.mesh.material = new MeshPhongMaterial(materialOptions)
        ;(this.mesh.material as MeshPhongMaterial).wireframe = false
        break
      case 'normal':
        this.mesh.material = new MeshNormalMaterial(materialOptions)
        ;(this.mesh.material as MeshNormalMaterial).wireframe = false
        break
    }

    // This is annoying, but a material can technically either be a Material or an array of them.
    // We only ever use a single material, so we can safely cast it to a Material.
    const meshMaterial = this.mesh.material as Material

    meshMaterial.side = this.info.side
    meshMaterial.needsUpdate = true
  }
}

export default Renderable
